<template>
  <div
    ref="navHeaderWrapper"
    :class="{'header-loading': loading, 'header-loading-out-of-viewport': loading && headerOutOfViewportEnabled}"
  >
    <div
      id="nav-header"
      ref="navHeader"
      :data-env-props="dataEnvProps"
      :data-context="context"
    />
    <LoadingComponent
      v-if="loading"
      height="100%"
    />
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, onBeforeMount, onBeforeUnmount, watch,
} from 'vue';
import { useStore } from 'vuex';
import LoadingComponent from '@common-vue/bag-checkout-common-consumable/src/components/LoadingComponent';

const store = useStore();

const BRAND = process.env.ENTRY_POINT || 'mcom';
const navHeader = ref(null);
const navHeaderWrapper = ref(null);
const loading = ref(true);
const adBannerRendered = ref(false);
// const observer = ref(null);
const initialHeaderWrapperHeight = ref(0);
let animationFrame;

const props = defineProps({
  assetHost: {
    type: String,
    required: true,
  },
  context: {
    type: String,
    required: true,
  },
});

const headerOutOfViewportEnabled = computed(() => store.state.killswitches.headerOutOfViewportEnabled);

const navigationAdBannerRenderedHandler = () => {
  adBannerRendered.value = true;
};

const headerScrollCallback = () => {
  const height = navHeader.value.offsetHeight;
  let difference = height - initialHeaderWrapperHeight.value;
  if (headerOutOfViewportEnabled.value) {
    difference += initialHeaderWrapperHeight.value;
  }
  loading.value = false;

  window.scrollBy({ top: difference });

  window.removeEventListener('navigationAdBannerRendered', navigationAdBannerRenderedHandler, { once: true });
};

let observer = new MutationObserver(() => {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame);
  }
  animationFrame = requestAnimationFrame(() => {
    const height = navHeader.value?.offsetHeight;
    // sometimes style 'maximum-height: 0' is added to the header DOM
    if (height) {
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      if (adBannerRendered.value) {
        headerScrollCallback();
      } else {
        let unwatch = watch(
          () => adBannerRendered.value,
          (bannerRenderedNewVal) => {
            if (bannerRenderedNewVal) {
              headerScrollCallback();
              unwatch();
              unwatch = null;
            }
          },
        );

        setTimeout(() => {
          if (unwatch !== null) {
            headerScrollCallback();
            unwatch = null;
          }
        }, 2000);
      }
    }
  });
});

const dataEnvProps = computed(() => {
  const envProps = {
    brand: BRAND,
    hostlink: document.location.origin,
    assethost: props.assetHost,
  };
  return JSON.stringify(envProps);
});

onBeforeMount(() => {
  window.addEventListener('navigationAdBannerRendered', navigationAdBannerRenderedHandler, { once: true });
});

onMounted(() => {
  if (navHeader.value) {
    // https://web.dev/avoid-large-complex-layouts-and-layout-thrashing/#avoid-forced-synchronous-layouts
    requestAnimationFrame(() => {
      if (navHeaderWrapper.value) {
        initialHeaderWrapperHeight.value = navHeaderWrapper.value.offsetHeight;
      }
    });

    // Start observing the target node for configured mutations
    observer.observe(navHeader.value, { childList: true, subtree: true });
  }
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
  }
});

</script>

<style src="./css/common.scss" scoped />
